import { useCallback, useRef } from "react";
import api from "../api";

export const useChatFeedback = ({ debounceMs = 800 } = {}) => {
  const lastSubmitAtRef = useRef(new Map()); // key -> ms
  const inFlightRef = useRef(new Set()); // key

  const submitFeedback = useCallback(
    async ({ session_id, message_id, feedback, comment, response, model }) => {
      const normalizedSessionId = String(session_id || "").trim();
      const normalizedMessageId = String(message_id || "").trim();
      const normalizedFeedback =
        feedback == null ? null : String(feedback || "").trim().toLowerCase();

      const key = `${normalizedSessionId}:${normalizedMessageId}`;
      const now = Date.now();

      if (!normalizedSessionId || !normalizedMessageId) {
        const err = new Error("Missing session_id or message_id");
        err.code = "VALIDATION";
        throw err;
      }

      if (normalizedFeedback != null && !["like", "dislike"].includes(normalizedFeedback)) {
        const err = new Error('feedback must be "like", "dislike", or null');
        err.code = "VALIDATION";
        throw err;
      }

      if (inFlightRef.current.has(key)) {
        return { ok: false, skipped: true, reason: "in_flight" };
      }

      const lastAt = lastSubmitAtRef.current.get(key) || 0;
      if (debounceMs > 0 && now - lastAt < debounceMs) {
        return { ok: false, skipped: true, reason: "debounced" };
      }

      inFlightRef.current.add(key);
      lastSubmitAtRef.current.set(key, now);
      try {
        // Clearing feedback: send minimal payload; backend will delete the record.
        if (normalizedFeedback == null) {
          const res = await api.post(`/chat/feedback`, {
            session_id: normalizedSessionId,
            message_id: normalizedMessageId,
            feedback: null,
          });
          return { ok: true, data: res.data };
        }

        const res = await api.post(`/chat/feedback`, {
          session_id: normalizedSessionId,
          message_id: normalizedMessageId,
          feedback: normalizedFeedback,
          comment: comment == null ? null : String(comment),
          response: String(response || ""),
          model: String(model || ""),
        });
        return { ok: true, data: res.data };
      } finally {
        inFlightRef.current.delete(key);
      }
    },
    [debounceMs],
  );

  return { submitFeedback };
};
